import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { ApiError } from '@app/shared';
import i18n from '@/shared/i18n';
import ruErrors from '@/shared/i18n/ru/errors.json';

export type ApiQueryError = FetchBaseQueryError | SerializedError | undefined;
type ErrorMessageKey = keyof typeof ruErrors;

function isApiError(data: unknown): data is ApiError {
  return (
    typeof data === 'object' && data !== null && 'code' in data && typeof data.code === 'string'
  );
}

/** Extracts the server's `ApiError.code` from an RTK Query error, if there is one. */
export function getErrorCode(error: ApiQueryError): string | undefined {
  if (!error || !('status' in error)) return undefined;
  return isApiError(error.data) ? error.data.code : undefined;
}

/**
 * Maps an RTK Query error to a translated message via the `errors` namespace, falling back to the
 * raw server message and then a generic string. Reads from the shared `i18n` singleton directly
 * (the `errors` namespace is always eagerly loaded — see shared/i18n/index.ts — unlike `admin`,
 * which is fetched on demand) instead of taking the caller's own `t`, since react-i18next's
 * `TFunction` is branded per the caller's specific namespace tuple and doesn't have a useful common
 * supertype to accept here.
 */
export function translateApiError(error: ApiQueryError): string {
  if (!error || !('status' in error)) return i18n.t('errors:default');
  if (error.status === 'FETCH_ERROR' || error.status === 'TIMEOUT_ERROR')
    return i18n.t('errors:network');

  const code = getErrorCode(error);
  if (code && code in ruErrors) return i18n.t(`errors:${code as ErrorMessageKey}`);
  if (isApiError(error.data) && error.data.message) return error.data.message;
  return i18n.t('errors:default');
}
